<template>
  <div class="login-container">
    <div class="login-card">
      <div class="login-header">
        <h2>C学</h2>
        <p>我们要筑基</p>
      </div>
      
      <el-form 
        ref="loginForm" 
        :model="loginForm" 
        :rules="loginRules" 
        class="login-form"
        @submit.native.prevent="handleLogin"
      >
        <el-form-item prop="username">
          <el-input
            v-model="loginForm.username"
            placeholder="请输入用户名"
            prefix-icon="el-icon-user"
            size="large"
            clearable
          />
        </el-form-item>
        
        <el-form-item prop="password">
          <el-input
            v-model="loginForm.password"
            type="password"
            placeholder="请输入密码"
            prefix-icon="el-icon-lock"
            size="large"
            show-password
            @keyup.enter.native="handleLogin"
          />
        </el-form-item>
        
        <el-form-item>
          <el-checkbox v-model="loginForm.remember">记住我</el-checkbox>
        </el-form-item>
        
        <el-form-item>
          <el-button
            type="primary"
            :loading="loading"
            class="login-btn"
            @click="handleLogin"
          >
            {{ loading ? '登录中...' : '登录' }}
          </el-button>
        </el-form-item>
      </el-form>
    </div>
  </div>
</template>

<script>
import CryptoJS from 'crypto-js'
import JSEncrypt from 'jsencrypt'
import stockApi from '@/api/stockApi'

export default {
  name: 'LoginPage',
  data() {
    return {
      loginForm: {
        username: '',
        password: '',
        remember: false
      },
      loginRules: {
        username: [
          { required: true, message: '请输入用户名', trigger: 'blur' },
          { min: 2, max: 20, message: '用户名长度在 2 到 20 个字符', trigger: 'blur' }
        ],
        password: [
          { required: true, message: '请输入密码', trigger: 'blur' },
          { min: 4, max: 20, message: '密码长度在 4 到 20 个字符', trigger: 'blur' }
        ]
      },
      loading: false
    }
  },
  mounted() {
    // 如果有记住的用户名，自动填充
    const savedUsername = localStorage.getItem('remembered_username')
    if (savedUsername) {
      this.loginForm.username = savedUsername
      this.loginForm.remember = true
    }
  },
  methods: {
    encryptPassword(password, publicKey) {
      const passwordMd5 = CryptoJS.MD5(String(password || '')).toString()
      const encryptor = new JSEncrypt()
      encryptor.setPublicKey(publicKey)
      const encrypted = encryptor.encrypt(passwordMd5)
      if (!encrypted) {
        throw new Error('密码加密失败')
      }
      return encrypted
    },

    handleLogin() {
      this.$refs.loginForm.validate(async (valid) => {
        if (!valid) return
        
        this.loading = true
        
        try {
          const keyResp = await stockApi.getLoginPublicKey()
          const publicKey = keyResp?.publicKey
          if (!publicKey) {
            throw new Error('获取登录密钥失败')
          }

          const encryptedPassword = this.encryptPassword(this.loginForm.password, publicKey)
          const loginResp = await stockApi.login({
            username: this.loginForm.username,
            password: encryptedPassword
          })
          const token = loginResp?.token
          const userInfo = loginResp?.userInfo
          if (!token || !userInfo) {
            throw new Error(loginResp?.message || '登录失败')
          }
          
          // 存储 token 和用户信息
          localStorage.setItem('token', token)
          localStorage.setItem('userInfo', JSON.stringify(userInfo))
          
          // 记住用户名
          if (this.loginForm.remember) {
            localStorage.setItem('remembered_username', this.loginForm.username)
          } else {
            localStorage.removeItem('remembered_username')
          }
          
          this.$message.success('登录成功')
          
          // 跳转到首页或之前的页面
          const redirect = this.$route.query.redirect || '/gap-up-analysis'
          this.$router.push(redirect)
          
        } catch (error) {
          if (error && error.crashed) return
          this.$message.error(error.message || '登录失败，请重试')
        } finally {
          this.loading = false
        }
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.login-container {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, rgba(102, 126, 234, 0.85) 0%, rgba(118, 75, 162, 0.85) 100%),
              url('~@/assets/images/login.jpg');
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  padding: 20px;
  
  .login-card {
    width: 100%;
    max-width: 400px;
    background: #fff;
    border-radius: 12px;
    padding: 40px;
    box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
    
    .login-header {
      text-align: center;
      margin-bottom: 30px;
      
      h2 {
        margin: 0;
        font-size: 32px;
        color: #333;
        font-weight: 600;
      }
      
      p {
        margin: 10px 0 0;
        color: #999;
        font-size: 14px;
      }
    }
    
    .login-form {
      .el-form-item {
        margin-bottom: 22px;
        
        &:last-child {
          margin-bottom: 0;
        }
      }
      
      .el-input {
        :deep(.el-input__inner) {
          height: 44px;
          line-height: 44px;
          padding-left: 40px;
        }
        
        :deep(.el-input__prefix) {
          left: 10px;
          
          .el-input__icon {
            line-height: 44px;
            font-size: 18px;
            color: #999;
          }
        }
      }
      
      .el-checkbox {
        color: #666;
      }
      
      .login-btn {
        width: 100%;
        height: 44px;
        font-size: 16px;
        border-radius: 6px;
      }
    }
  }
}

// 移动端适配
@media screen and (max-width: 768px) {
  .login-container {
    padding: 15px;
    
    .login-card {
      padding: 30px 25px;
      border-radius: 10px;
      
      .login-header {
        h2 {
          font-size: 28px;
        }
      }
    }
  }
}

// 平板适配
@media screen and (min-width: 769px) and (max-width: 1024px) {
  .login-container {
    .login-card {
      max-width: 450px;
      padding: 35px;
    }
  }
}
</style>
